// src/components/session/SessionProgressBar.js

import React from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';

const ProgressBarSegment = ({ percentage, colorClass, positionClass }) => (
  <div
    style={{ width: `${percentage}%` }}
    className={`absolute top-0 h-full ${positionClass} ${colorClass} transition-all duration-500 ease-out`}
  ></div>
);

const SessionProgressBar = ({ correct, incorrect, total: providedTotal, compact = false }) => {
  // Treat incorrect as 0 if it's not provided or is null/undefined
  const actualIncorrect = incorrect || 0;

  // Use provided total if available, otherwise calculate from remaining
  const total = providedTotal;

  if (total === 0) return null;

  const correctPercent = (correct / total) * 100;
  const incorrectPercent = (actualIncorrect / total) * 100;

  const itemsDone = correct + actualIncorrect;

  if (compact) {
    return (
      <div className="flex items-center w-full px-1">
        {/* Mobile Compact Visual Bar - all info in one bar */}
        <div className="relative flex-grow h-4 bg-background-primary rounded-md overflow-hidden border border-border">
          {/* Green segment, anchored to the left */}
          <ProgressBarSegment percentage={correctPercent} colorClass="bg-green-500" positionClass="left-0" />

          {/* Red segment, anchored to the right, only if there are incorrect items */}
          {actualIncorrect > 0 && <ProgressBarSegment percentage={incorrectPercent} colorClass="bg-red-500" positionClass="right-0" />}

          {/* Mobile compact info overlay - optimized spacing */}
          <div className="absolute inset-0 flex justify-center items-center px-2">
            <div className="flex items-center gap-2 text-xs font-semibold">
              {/* Correct count - no icon, just number */}
              <div className="text-green-700 dark:text-green-300 bg-white/80 dark:bg-black/70 px-1.5 py-0.5 rounded shadow-sm backdrop-blur-sm">
                <span className="text-xs font-bold">{correct}</span>
              </div>
              
              {/* Progress text - with opacity background */}
              <span className="text-content-primary bg-white/80 dark:bg-black/70 px-1.5 py-0.5 rounded text-xs font-bold shadow-sm backdrop-blur-sm">
                {itemsDone}/{total}
              </span>
              
              {/* Incorrect count - no icon, just number - only if > 0 */}
              {actualIncorrect > 0 && (
                <div className="text-red-700 dark:text-red-300 bg-white/80 dark:bg-black/70 px-1.5 py-0.5 rounded shadow-sm backdrop-blur-sm">
                  <span className="text-xs font-bold">{actualIncorrect}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 w-full">
      {/* Correct Count */}
      <div className="flex items-center gap-1.5 text-green-500 font-semibold">
        <CheckIcon className="h-5 w-5" />
        <span className="text-sm w-4 text-center">{correct}</span>
      </div>

      {/* The Visual Bar Wrapper */}
      <div className="relative flex-grow h-4 bg-background-primary rounded-full overflow-hidden border border-border">
        {/* Green segment, anchored to the left */}
        <ProgressBarSegment percentage={correctPercent} colorClass="bg-green-500" positionClass="left-0" />

        {/* Red segment, anchored to the right, only if there are incorrect items */}
        {actualIncorrect > 0 && <ProgressBarSegment percentage={incorrectPercent} colorClass="bg-red-500" positionClass="right-0" />}

        {/* Absolute positioned text sits on top */}
        <div className="absolute inset-0 flex justify-center items-center ">
          <span className="text-content-primary bg-white/80 dark:bg-black/70 px-1.5 py-0.5 rounded text-xs font-bold shadow-sm backdrop-blur-sm">
            {itemsDone} / {total}
          </span>
        </div>
      </div>

      {/* Incorrect Count, only display if there are incorrect items */}
      {actualIncorrect > 0 && (
        <div className="flex items-center gap-1.5 text-red-500 font-semibold">
          <span className="text-sm w-4 text-center">{actualIncorrect}</span>
          <XMarkIcon className="h-5 w-5" />
        </div>
      )}
    </div>
  );
};

export default SessionProgressBar;
